import { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { ChevronRight, Menu, Sparkles, LogIn, UserPlus, LogOut, LayoutDashboard, Sun, Moon } from "lucide-react";
import MobileMenuDrawer from "@/components/MobileMenuDrawer";
import MegaNav, { MOBILE_DRAWER_ITEMS } from "@/components/MegaNav";
import SiteFooter from "@/components/SiteFooter";
import useAppReady from "@/lib/useAppReady";

const LOGO = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

/**
 * Shared layout for every public-facing page (Resources, Explore, Courses,
 * About, FAQs, Privacy, Terms, Books, Videos, etc.). It renders the same
 * desktop MegaNav + mobile drawer as the landing page so the experience is
 * consistent everywhere a visitor can wander.
 */
export default function PublicShell() {
  useAppReady();
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const dashboardPath = user?.role === "admin" ? "/admin" : "/activities";

  // When a visitor lands on a course page and clicks Sign in / Get
  // started from the nav, keep them anchored to that course after
  // auth so they can enrol without losing their spot. Any other public
  // page (Resources, About, …) sends them to the dashboard by default.
  const onCoursePath = pathname === "/courses" || pathname.startsWith("/courses/");
  const nextQuery = onCoursePath ? `?next=${encodeURIComponent(pathname)}` : "";
  const loginTo = `/login${nextQuery}`;
  const signupTo = `/signup${nextQuery}`;

  // Features defaults open only on the landing page; everywhere else it
  // starts collapsed so the drawer reads quiet. (TopGroup also auto-expands
  // if a child matches the current route.) Logged-in visitors don't see
  // the Free Screening lead-magnet — they already have the full flow.
  const drawerItems = MOBILE_DRAWER_ITEMS.map((it) => {
    let out = it;
    if (it.label === "Features") out = { ...out, defaultOpen: false };
    if (user && it.label === "Free Tools" && Array.isArray(it.children)) {
      out = { ...out, children: it.children.filter((c) => c.to !== "/free-screening") };
    }
    return out;
  });

  const drawerUser = user
    ? {
        name: user.full_name || user.email,
        email: user.email,
        planBadge: user.plan === "pro" ? "PRO" : "FREE",
        avatar: user.photo,
        profileLink: "/edit-profile",
      }
    : null;

  const drawerFooter = user ? (
    <div className="space-y-2">
      <Button
        onClick={() => { setOpen(false); navigate(dashboardPath); }}
        className="w-full bg-coral hover:bg-coral-dark text-white rounded-full font-bold py-5"
        data-testid="drawer-go-dashboard"
      >
        <LayoutDashboard className="w-4 h-4 mr-1.5" /> Go to dashboard
      </Button>
      <Button
        variant="outline"
        onClick={() => { setOpen(false); toggleTheme(); }}
        className="w-full rounded-full border-2 border-plum/10 text-inkmid hover:bg-plum/5 font-semibold py-4"
        data-testid="drawer-theme-toggle"
      >
        {theme === "dark" ? <Sun className="w-4 h-4 mr-1.5" /> : <Moon className="w-4 h-4 mr-1.5" />}
        {theme === "dark" ? "Light mode" : "Dark mode"}
      </Button>
      <Button
        variant="outline"
        onClick={() => { setOpen(false); logout(); navigate("/"); }}
        className="w-full rounded-full border-2 border-plum/10 text-inkmid hover:bg-plum/5 font-bold py-5"
        data-testid="drawer-logout"
      >
        <LogOut className="w-4 h-4 mr-1.5" /> Log out
      </Button>
    </div>
  ) : (
    <div className="space-y-2">
      <Link to={signupTo} onClick={() => setOpen(false)} className="block">
        <Button className="w-full bg-coral hover:bg-coral-dark text-white rounded-full font-bold py-5" data-testid="drawer-signup">
          <UserPlus className="w-4 h-4 mr-1.5" /> Get started — free
        </Button>
      </Link>
      <Link to={loginTo} onClick={() => setOpen(false)} className="block">
        <Button
          variant="outline"
          className="w-full rounded-full border-2 border-coral/30 text-coral hover:bg-coral/10 hover:border-coral font-bold py-5 bg-transparent"
          data-testid="drawer-login"
        >
          <LogIn className="w-4 h-4 mr-1.5" /> Sign in
        </Button>
      </Link>
      <Button
        variant="outline"
        onClick={() => { setOpen(false); toggleTheme(); }}
        className="w-full rounded-full border-2 border-plum/10 text-inkmid hover:bg-plum/5 font-semibold py-4"
        data-testid="drawer-theme-toggle"
      >
        {theme === "dark" ? <Sun className="w-4 h-4 mr-1.5" /> : <Moon className="w-4 h-4 mr-1.5" />}
        {theme === "dark" ? "Light mode" : "Dark mode"}
      </Button>
    </div>
  );

  // Hide the public nav on mobile for logged-in users on the 4 "Free Tools" pages —
  // they get the KPMobileBackHeader from the page itself instead.
  const KP_TOOL_PATHS = ["/resources", "/milestones", "/child-age-calculator", "/ai-video-assessment", "/help-center", "/faqs"];
  const hideNavOnMobile = !!user && KP_TOOL_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

  return (
    <div className="min-h-screen bg-cream">
      <header className={`glass-header sticky top-0 z-40${hideNavOnMobile ? " hidden md:block" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="public-logo">
            <img src={LOGO} alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded-[22%]" />
            <img src={WORDMARK} alt="Kiddo+" className="h-6 sm:h-7 w-auto" />
          </Link>

          {/* Desktop nav — same MegaNav on every public page */}
          <MegaNav />

          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-coral/10 text-inkmid hover:text-coral transition-colors"
              data-testid="public-theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {user ? (
              <Button
                onClick={() => navigate(dashboardPath)}
                size="sm"
                className="bg-coral hover:bg-coral-dark text-white rounded-full font-bold gap-1 px-3 sm:px-4 ml-1 whitespace-nowrap"
                data-testid="public-go-dashboard"
              >
                Go to dashboard <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Link to={loginTo} data-testid="public-nav-login">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full border-2 border-coral/30 text-coral hover:bg-coral/10 hover:border-coral font-bold px-4 whitespace-nowrap bg-transparent"
                  >
                    Sign in
                  </Button>
                </Link>
                <Link to={signupTo}>
                  <Button size="sm" className="bg-coral hover:bg-coral-dark text-white rounded-full font-bold px-4 sm:px-5 whitespace-nowrap shadow-soft-sm" data-testid="public-nav-signup">
                    Get started <ChevronRight className="w-4 h-4 ml-0.5" />
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile: condensed CTA + hamburger drawer trigger */}
          <div className="flex md:hidden items-center gap-1">
            {user ? (
              <Button
                onClick={() => navigate(dashboardPath)}
                size="sm"
                className="bg-coral hover:bg-coral-dark text-white rounded-full font-bold px-3 whitespace-nowrap"
                data-testid="public-go-dashboard-mobile"
              >
                Dashboard
              </Button>
            ) : null}
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-coral/10 text-inkdark ml-1 active:scale-95 transition"
              data-testid="public-mobile-toggle"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenuDrawer
        open={open}
        onClose={() => setOpen(false)}
        user={drawerUser}
        items={drawerItems}
        footer={drawerFooter}
        brand={{ logo: LOGO, wordmark: WORDMARK }}
      />

      <main>
        {/* Each page handles its own width — Resources/Explore go full-bleed,
            Courses uses max-w containers internally. */}
        <Outlet />
      </main>

      <SiteFooter />

      {/* Mobile sticky CTA — only on /resources & /explore-activities */}
      {(pathname === "/resources" || pathname === "/explore-activities") && (
        !user ? (
          <Link to="/signup" className="md:hidden fixed left-3 right-3 bottom-3 z-40 block" data-testid="public-sticky-cta">
            <Button className="w-full bg-coral hover:bg-coral-dark text-white rounded-full py-5 font-bold text-sm shadow-soft-lg">
              <Sparkles className="w-4 h-4 mr-1.5" /> Get Personalised Guidance — Start Free Today
            </Button>
          </Link>
        ) : user.plan !== "pro" ? (
          <Link to="/subscription" className="md:hidden fixed left-3 right-3 bottom-3 z-40 block" data-testid="public-sticky-upgrade">
            <Button className="w-full bg-coral hover:bg-coral-dark text-white rounded-full py-5 font-bold text-sm shadow-soft-lg">
              <Sparkles className="w-4 h-4 mr-1.5" /> Upgrade to Pro
            </Button>
          </Link>
        ) : null
      )}
    </div>
  );
}
